import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  QrCode, Scales, Trash, Plus, CheckCircle, User,
  Keyboard, Receipt, Warning, X, ArrowRight, Camera,
  MagnifyingGlass, Phone, Package as PackageIcon,
} from "@phosphor-icons/react";
import { staffApi, getGps, formatStaffErr } from "../../lib/staffApi";
import { Html5Qrcode } from "html5-qrcode";
import ThermalPrintButton from "../../components/print/ThermalPrintButton";
import { buildPosReceipt } from "../../lib/receiptBuilder";
import LiveScaleModal from "../../components/pos/LiveScaleModal";

/**
 * POS · walk-up billing.
 *
 * Two entry modes, both land on the same "create-invoice" endpoint:
 *
 *   A. **Bag-first**  — staff scans/types a QR code first. First bag
 *      locks the customer. Add more bags. Submit.
 *
 *   B. **Phone-first (Iter 214)** — staff types the customer's phone
 *      number in the picker, selects the match, and the full invoice
 *      form appears: assigned QR bags with weight inputs, non-QR
 *      loose-weight lines, extra-item chips, live total. Mirrors the
 *      Task-Detail collection screen so operators only learn one flow.
 */
export default function StaffPOS() {
  const nav = useNavigate();
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCode, setScanCode] = useState("");
  const [scanWeight, setScanWeight] = useState("");
  const [scanManual, setScanManual] = useState(false);
  const [cameraMode, setCameraMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [bags, setBags] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Iter 214 — phone-first POS state
  const [ctx, setCtx]                 = useState(null);   // customer-context payload
  const [ctxLoading, setCtxLoading]   = useState(false);
  const [assignedBagWeights, setAssignedBagWeights] = useState({}); // qr_code → kg string
  const [nonQrWeights, setNonQrWeights] = useState({});   // waste_type_id → kg string
  const [selectedExtras, setSelectedExtras] = useState([]);         // extra_charge_id[]
  // Iter 217 — after picking a customer via phone, gate the invoice
  // form behind an explicit "Start Job" click. Two reasons:
  //  1. Ceremony — operator confirms it's the right customer before
  //     touching any weight input (fat-finger safety).
  //  2. Job start timestamp — captured client-side so we can report
  //     the operator's per-job cycle time from Duty analytics later.
  const [jobStarted, setJobStarted] = useState(false);
  const [jobStartedAt, setJobStartedAt] = useState(null);

  // Iter 214 — whenever a customer is picked (via search) AND no bag
  // has been scanned yet, fetch the full context so we can show the
  // phone-first invoice form. If the operator later scans a bag, we
  // keep the same context (it doesn't change).
  useEffect(() => {
    if (!customer || bags.length > 0) { return; }
    let alive = true;
    setCtxLoading(true);
    staffApi.get(`/staff-app/pos/customer-context/${customer.id}`)
      .then((r) => {
        if (!alive) return;
        setCtx(r.data);
        if (!pkg && r.data?.package) setPkg(r.data.package);
        // Reset any stale weights + job flag when the customer changes.
        setAssignedBagWeights({});
        setNonQrWeights({});
        setSelectedExtras([]);
        setJobStarted(false);
        setJobStartedAt(null);
      })
      .catch(() => { if (alive) setCtx(null); })
      .finally(() => { if (alive) setCtxLoading(false); });
    return () => { alive = false; };
  }, [customer, bags.length, pkg]);

  const resetScan = () => {
    setScanCode(""); setScanWeight(""); setScanManual(false);
    setCameraMode(false); setScanOpen(false);
  };

  const doScan = async () => {
    const code = (scanCode || "").trim().toUpperCase();
    if (!code) { toast.error("Enter a QR code"); return; }
    const w = Number(scanWeight);
    if (!(w > 0)) { toast.error("Weight must be > 0"); return; }
    if (bags.some(b => b.qr_code === code)) {
      toast.error("Bag already scanned"); return;
    }
    setBusy(true);
    try {
      const r = await staffApi.post("/staff-app/pos/scan-bag", { qr_code: code });
      const b = r.data.bag;
      const c = r.data.customer;
      const p = r.data.package;
      const wm = r.data.waste || {};
      if (customer && c && c.id !== customer.id) {
        toast.error("Bag belongs to another customer"); setBusy(false); return;
      }
      // First bag locks the customer & package. If bag has none — keep any
      // manually-picked customer.
      if (!customer && c) setCustomer(c);
      if (!pkg && p) setPkg(p);
      setBags(bs => [...bs, {
        qr_code: b.qr_code,
        weight_kg: w,
        waste_type_id: wm.waste_type_id,
        category: wm.waste_category,
        name: wm.waste_name,
        manual: scanManual,
      }]);
      toast.success(`Bag ${b.qr_code} added`);
      if (!c && !customer) {
        toast.message("This bag has no assigned customer. Pick one below.");
        setPickerOpen(true);
      }
      resetScan();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || formatStaffErr(e);
      toast.error(String(msg));
    }
    setBusy(false);
  };

  const removeBag = (code) => {
    const next = bags.filter(b => b.qr_code !== code);
    setBags(next);
    if (next.length === 0) { setCustomer(null); setPkg(null); }
  };

  const submit = useCallback(async () => {
    if (!customer) { toast.error("Pick a customer first"); setPickerOpen(true); return; }

    // Iter 214 — collect QR bag lines from BOTH sources:
    //  · `bags[]` — bags added via the classic scan flow
    //  · assigned bags with a weight typed in the phone-first form
    const assignedLines = Object.entries(assignedBagWeights)
      .map(([qr, wStr]) => ({ qr, w: Number(wStr) }))
      .filter(({ w }) => w > 0)
      .map(({ qr, w }) => {
        const b = (ctx?.assigned_bags || []).find(x => x.qr_code === qr);
        return {
          qr_code: qr,
          weight_kg: w,
          waste_type_id: b?.waste_type_id,
          manual: false,
        };
      });
    const scanLines = bags.map(b => ({
      qr_code: b.qr_code,
      weight_kg: Number(b.weight_kg),
      waste_type_id: b.waste_type_id,
      manual: b.manual,
    }));
    // Dedupe: scan flow takes precedence over an assigned-bag entry
    // with the same QR (very unlikely, but safe).
    const seen = new Set(scanLines.map(l => l.qr_code));
    const combinedBagLines = [...scanLines, ...assignedLines.filter(l => !seen.has(l.qr_code))];

    const nonQrLines = Object.entries(nonQrWeights)
      .map(([wtId, wStr]) => ({ waste_type_id: wtId, quantity_kg: Number(wStr) }))
      .filter(l => l.quantity_kg > 0);

    if (combinedBagLines.length === 0 && nonQrLines.length === 0 && selectedExtras.length === 0) {
      toast.error("Enter at least one weight or extra charge");
      return;
    }

    setSubmitting(true);
    try {
      const gps = await getGps().catch(() => ({ latitude: 0, longitude: 0, accuracy: null, address: null }));
      const r = await staffApi.post("/staff-app/pos/create-invoice", {
        customer_id: customer.id,
        package_id: pkg?.id || null,
        bags: combinedBagLines,
        non_qr_lines: nonQrLines,
        extra_charge_ids: selectedExtras,
        notes,
        latitude: gps.latitude, longitude: gps.longitude,
        gps_accuracy: gps.accuracy || null, gps_address: gps.address || null,
      });
      setResult(r.data);
      toast.success(`Invoice ${r.data.invoice_no} created`);
    } catch (e) { toast.error(formatStaffErr(e)); }
    setSubmitting(false);
  }, [bags, customer, pkg, notes, assignedBagWeights, nonQrWeights, selectedExtras, ctx]);

  const totalKg = bags.reduce((a, b) => a + Number(b.weight_kg || 0), 0);

  // Iter 218 — client-side total preview. Rules:
  //   · Σ(kg × customer_rate) for every scanned/assigned/non-QR line
  //   · Σ(extras) — HONOURING QUANTITIES because selectedExtras is now
  //     an ARRAY OF IDS with duplicates allowed (backend iterates the
  //     list so 2× the same id → 2× the charge).
  //   · Package base price (`pkg.price`) added ONCE when any billable
  //     line exists, mirroring how `_apply_package_pricing` behaves
  //     server-side.
  // The backend re-computes authoritatively; this is only for UX.
  const totalPreview = useMemo(() => {
    const rateOf = (wtId) => {
      const wt = (ctx?.included_types || []).find(t => t.waste_type_id === wtId);
      return wt ? Number(wt.customer_rate || wt.rate_per_kg || 0) : 0;
    };
    let subtotal = 0;
    let hasBillable = false;
    // Scanned bags
    bags.forEach(b => {
      const w = Number(b.weight_kg || 0);
      if (w > 0) hasBillable = true;
      subtotal += w * rateOf(b.waste_type_id);
    });
    // Assigned bag weight entries
    Object.entries(assignedBagWeights).forEach(([qr, w]) => {
      const wv = Number(w || 0);
      if (wv > 0) hasBillable = true;
      const ab = (ctx?.assigned_bags || []).find(x => x.qr_code === qr);
      subtotal += wv * rateOf(ab?.waste_type_id);
    });
    // Non-QR lines
    Object.entries(nonQrWeights).forEach(([wtId, w]) => {
      const wv = Number(w || 0);
      if (wv > 0) hasBillable = true;
      subtotal += wv * rateOf(wtId);
    });
    // Extras — iterate array so duplicates count.
    selectedExtras.forEach(eid => {
      const x = (ctx?.extra_charges || []).find(e => e.id === eid);
      if (x) { subtotal += Number(x.amount || 0); hasBillable = true; }
    });
    // Package base price — added once if anything is billable, matches
    // finance_routes._apply_package_pricing() server-side behaviour.
    const pkgPrice = Number(ctx?.package?.price || 0);
    if (hasBillable && pkgPrice > 0) subtotal += pkgPrice;
    return subtotal;
  }, [ctx, bags, assignedBagWeights, nonQrWeights, selectedExtras]);

  // Success screen
  if (result) {
    return (
      <div className="space-y-5" data-testid="staff-pos-success">
        <div className="rounded-xl bg-[#2D5A27] text-white p-6 text-center">
          <CheckCircle size={44} weight="fill" className="inline-block mb-2" />
          <div className="font-heading text-2xl">Invoice created</div>
          <div className="text-xs opacity-80 mt-1 uppercase tracking-[0.16em]">
            {result.status === "draft" ? "Draft · pending office verification" : "Issued"}
          </div>
          <div className="font-mono text-lg mt-4">{result.invoice_no}</div>
          <div className="font-heading text-4xl mt-2">₹{Number(result.total).toFixed(2)}</div>
          <div className="text-xs opacity-80 mt-1">
            {result.bag_codes.length} bag{result.bag_codes.length > 1 ? "s" : ""} · {result.customer?.name}
          </div>
        </div>
        <ThermalPrintButton
          label="Print receipt (Bluetooth)"
          variant="primary"
          dataTestId="pos-thermal-print"
          className="w-full h-12"
          build={() => buildPosReceipt(result, { staff_name: result.staff_name })}
        />
        <button onClick={() => { setResult(null); setBags([]); setCustomer(null); setPkg(null); setNotes(""); }}
                data-testid="pos-new-session"
                className="w-full h-12 bg-[#0F1F0C] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
          <Plus size={14} weight="bold" />Start a new POS session
        </button>
        <button onClick={() => nav("/staff-app")}
                className="w-full h-11 border border-[#E5E2D9] rounded-md text-sm text-[#5C5A55]">
          Back to duty
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="staff-pos">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">
          Field POS · walk-up billing
        </div>
        <h1 className="font-heading text-xl text-[#1A1A1A]">Scan bags, bill on the spot</h1>
      </div>

      {/* Customer / package summary */}
      {customer ? (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-4" data-testid="pos-customer">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#D4E0D1] flex items-center justify-center">
              <User size={18} color="#2D5A27" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-base truncate">{customer.name}</div>
              {customer.phone && <div className="text-xs text-[#5C5A55] font-mono">{customer.phone}</div>}
              {pkg && (
                <div className="text-[11px] text-[#2D5A27] mt-1">
                  Package: <strong>{pkg.name}</strong>
                  {pkg.price ? <span className="text-[#8A8780]"> · ₹{pkg.price}</span> : null}
                </div>
              )}
              {!pkg && (
                <div className="text-[11px] text-[#C25D38] mt-1">
                  <Warning size={10} className="inline -mt-0.5"/> No package assigned — billing will fail without one.
                </div>
              )}
            </div>
            <button onClick={() => { setCustomer(null); setPkg(null); }}
                    data-testid="pos-clear-customer"
                    className="p-2 text-[#C25D38] hover:bg-[#F9EDE7] rounded-md">
              <X size={14} />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button onClick={() => setPickerOpen(true)} data-testid="pos-start-by-phone"
                   className="w-full bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-lg p-4 text-sm font-medium inline-flex items-center justify-center gap-2">
            <Phone size={16} weight="fill"/>Start by phone number
          </button>
          <div className="text-center text-[10px] uppercase tracking-[0.24em] text-[#8A8780] py-1">or</div>
          <button onClick={() => setScanOpen(true)} data-testid="pos-start-by-scan"
                   className="w-full bg-[#F9E4B7]/40 border border-dashed border-[#F9E4B7] rounded-lg p-4 text-sm text-[#7A5A11] inline-flex items-center justify-center gap-2 hover:bg-[#F9E4B7]/60">
            <QrCode size={16}/>Scan first bag to auto-detect customer
          </button>
        </div>
      )}

      {/* Iter 214/217 — phone-first flow.
          Step A (customer picked, job not started yet):
              show a "Start Job" gate card with customer summary +
              package preview so the operator confirms it's the right
              person before we surface any weight inputs.
          Step B (job started, no bag scanned yet):
              render the full invoice form (assigned bags, non-QR
              weights, extras, live total).
          Once the operator scans a bag, we fall through to the
          classic bag list below. */}
      {customer && bags.length === 0 && !jobStarted && (
        <StartJobGate
          customer={customer} ctx={ctx} ctxLoading={ctxLoading}
          onStart={() => { setJobStarted(true); setJobStartedAt(Date.now()); }}
          onChangeCustomer={() => setPickerOpen(true)}
        />
      )}
      {customer && bags.length === 0 && jobStarted && (
        <PhonePosForm
          ctx={ctx} ctxLoading={ctxLoading}
          jobStartedAt={jobStartedAt}
          assignedBagWeights={assignedBagWeights}
          setAssignedBagWeights={setAssignedBagWeights}
          nonQrWeights={nonQrWeights}
          setNonQrWeights={setNonQrWeights}
          selectedExtras={selectedExtras}
          setSelectedExtras={setSelectedExtras}
        />
      )}

      {/* Bag list */}
      <div className="space-y-2" data-testid="pos-bags">
        {bags.map((b, i) => (
          <div key={b.qr_code} className="bg-white border border-[#E5E2D9] rounded-lg p-3 flex items-center gap-3" data-testid={`pos-bag-${b.qr_code}`}>
            <div className="w-8 h-8 rounded-md bg-[#D4E0D1] flex items-center justify-center text-xs font-heading text-[#2D5A27]">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm truncate">{b.qr_code}</div>
              <div className="text-[11px] text-[#5C5A55]">
                {b.category || "—"} · {b.name || "—"} · <span className="font-mono">{b.weight_kg} kg</span>
                {b.manual && <span className="ml-1 text-[#C25D38]">· manual</span>}
              </div>
            </div>
            <button onClick={() => removeBag(b.qr_code)} data-testid={`pos-bag-remove-${b.qr_code}`}
                    className="p-2 text-[#C25D38] hover:bg-[#F9EDE7] rounded-md">
              <Trash size={14} />
            </button>
          </div>
        ))}
        {bags.length === 0 && (
          <div className="bg-white border border-dashed border-[#E5E2D9] rounded-lg p-8 text-center text-sm text-[#8A8780]">
            No bags scanned yet
          </div>
        )}
      </div>

      {/* Add bag button */}
      <button onClick={() => setScanOpen(true)} data-testid="pos-scan-btn"
              className="w-full h-14 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
        <QrCode size={18} weight="bold" />
        {bags.length === 0 ? "Scan first bag" : "Add another bag"}
      </button>

      {/* Notes */}
      {bags.length > 0 && (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">Notes (optional)</label>
          <textarea data-testid="pos-notes" value={notes} onChange={e => setNotes(e.target.value)}
                     placeholder="Any remark for office…" rows={2}
                     className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
        </div>
      )}

      {/* Totals + submit */}
      {(bags.length > 0 || (customer && (Object.values(assignedBagWeights).some(v => Number(v) > 0) || Object.values(nonQrWeights).some(v => Number(v) > 0) || selectedExtras.length > 0))) && (
        <div className="bg-[#0F1F0C] text-white rounded-lg p-4 space-y-3" data-testid="pos-summary">
          <div className="flex justify-between items-baseline">
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">Bags · total weight</div>
            <div className="font-heading text-xl"><Scales size={14} className="inline -mt-0.5"/> {bags.length + Object.values(assignedBagWeights).filter(v => Number(v) > 0).length + Object.values(nonQrWeights).filter(v => Number(v) > 0).length} · {(totalKg + Object.values(assignedBagWeights).reduce((a,v) => a + Number(v||0), 0) + Object.values(nonQrWeights).reduce((a,v) => a + Number(v||0), 0)).toFixed(2)} kg</div>
          </div>
          <div className="flex justify-between items-baseline">
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">Total payable (preview)</div>
            <div className="font-heading text-2xl" data-testid="pos-total-preview">
              ₹{totalPreview.toFixed(2)}
            </div>
          </div>
          <button onClick={submit} disabled={submitting || !customer}
                  data-testid="pos-submit"
                  className="w-full h-12 bg-[#C89E3E] hover:bg-[#B38A2F] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <Receipt size={16} weight="fill" />
            {submitting ? "Creating invoice…" : "Create invoice"}
            <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      )}

      {/* Scan modal */}
      {scanOpen && (
        <ScanModal
          scanCode={scanCode} setScanCode={setScanCode}
          scanWeight={scanWeight} setScanWeight={setScanWeight}
          scanManual={scanManual} setScanManual={setScanManual}
          cameraMode={cameraMode} setCameraMode={setCameraMode}
          onCancel={() => { resetScan(); }}
          onSubmit={doScan}
          busy={busy}
        />
      )}
      {pickerOpen && (
        <CustomerPicker
          onCancel={() => setPickerOpen(false)}
          onPick={(c, p) => { setCustomer(c); setPkg(p); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Scan modal with camera + manual entry
// ═════════════════════════════════════════════════════════════════════
function ScanModal({
  scanCode, setScanCode, scanWeight, setScanWeight, scanManual, setScanManual,
  cameraMode, setCameraMode, onCancel, onSubmit, busy,
}) {
  const readerRef = useRef(null);
  const scannerRef = useRef(null);
  const [cameraErr, setCameraErr] = useState("");
  const [scaleOpen, setScaleOpen] = useState(false);

  useEffect(() => {
    if (!cameraMode) return;
    let active = true;
    const id = "html5-qr-reader";
    const start = async () => {
      try {
        const el = document.getElementById(id);
        if (!el) return;
        const scanner = new Html5Qrcode(id, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decoded) => {
            if (!active) return;
            const cleaned = String(decoded || "").trim().toUpperCase();
            if (cleaned) {
              setScanCode(cleaned);
              setCameraMode(false);
              toast.success(`Scanned ${cleaned}`);
            }
          },
          () => {},
        );
      } catch (e) {
        setCameraErr(String(e?.message || e));
      }
    };
    start();
    return () => {
      active = false;
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => { s.clear?.(); });
      }
    };
  }, [cameraMode, setScanCode, setCameraMode]);

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={onCancel} data-testid="pos-scan-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg inline-flex items-center gap-1.5">
            <QrCode size={16} />Scan bag
          </div>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F7F6F2]">
            <X size={16} />
          </button>
        </div>

        {/* Camera scanner */}
        {cameraMode ? (
          <div className="space-y-2" data-testid="pos-camera-container">
            <div id="html5-qr-reader" ref={readerRef} className="w-full aspect-square bg-black rounded-md overflow-hidden"/>
            {cameraErr && (
              <div className="text-xs text-[#C25D38] bg-[#F9EDE7] rounded p-2">
                Camera error: {cameraErr}
              </div>
            )}
            <button onClick={() => setCameraMode(false)} data-testid="pos-camera-cancel"
                    className="w-full h-10 border border-[#E5E2D9] rounded-md text-sm">
              Type it instead
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">QR code</label>
              <div className="flex gap-2">
                <input data-testid="pos-scan-code" value={scanCode}
                        onChange={e => setScanCode(e.target.value.toUpperCase())}
                        placeholder="RSG-Q1-000001" autoFocus
                        className="flex-1 h-11 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono uppercase"/>
                <button onClick={() => { setCameraErr(""); setCameraMode(true); }} data-testid="pos-camera-open"
                        className="h-11 px-3 bg-[#2D5A27] text-white rounded-md text-sm font-medium inline-flex items-center gap-1.5">
                  <Camera size={16} weight="fill" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1.5">
                <Scales size={10} className="inline -mt-0.5 mr-0.5"/> Weight (kg)
              </label>
              <div className="flex gap-2">
                <input data-testid="pos-scan-weight" value={scanWeight} onChange={e => setScanWeight(e.target.value)}
                        placeholder="1.5" inputMode="decimal"
                        className="flex-1 h-11 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
                <button onClick={() => setScaleOpen(true)}
                        type="button"
                        data-testid="pos-scan-read-scale"
                        title="Read weight from paired scale (Bluetooth / USB-Serial)"
                        className="h-11 px-3 bg-[#1F3A57] hover:bg-[#15283B] text-white rounded-md text-xs font-medium inline-flex items-center gap-1.5">
                  <Scales size={14} weight="fill" />
                  Read scale
                </button>
              </div>
              <div className="text-[10px] text-[#8A8780] mt-1">
                Tap "Read scale" to auto-fill from your Bluetooth/USB weight scale.
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#5C5A55]">
              <input type="checkbox" checked={scanManual} onChange={e => setScanManual(e.target.checked)}
                      data-testid="pos-scan-manual"
                      className="accent-[#C25D38] w-4 h-4"/>
              <Keyboard size={12} />Manual entry (typed, not scanned)
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={onCancel} className="flex-1 h-11 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
              <button onClick={onSubmit} disabled={busy} data-testid="pos-scan-add"
                      className="flex-1 h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <Plus size={14} weight="bold" />{busy ? "…" : "Add bag"}
              </button>
            </div>
          </div>
        )}
      </div>
      <LiveScaleModal
        open={scaleOpen}
        onClose={() => setScaleOpen(false)}
        onUse={(kg) => { setScanWeight(String(kg)); setScaleOpen(false); toast.success(`Weight: ${kg} kg`); }}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Start-Job gate — customer confirmation card that gates the invoice
// form until the operator explicitly starts the walk-up job. (Iter 217)
// ═════════════════════════════════════════════════════════════════════
function StartJobGate({ customer, ctx, ctxLoading, onStart, onChangeCustomer }) {
  const pkg    = ctx?.package;
  const bagsN  = (ctx?.assigned_bags || []).length;
  const extras = (ctx?.extra_charges || []).length;
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-lg overflow-hidden"
          data-testid="pos-start-job-gate">
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-[#EDF7EA] text-[#2D5A27]
                          flex items-center justify-center shrink-0">
            <User size={22} weight="fill"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-lg text-[#1A1A1A] truncate">
              {customer.name || customer.customer_code || customer.phone}
            </div>
            <div className="text-[12px] text-[#5C5A55] truncate flex flex-wrap gap-x-2">
              {customer.phone && <span data-testid="pos-gate-phone">{customer.phone}</span>}
              {customer.customer_code && <span>· {customer.customer_code}</span>}
              {customer.address && <span className="truncate">· {customer.address}</span>}
            </div>
          </div>
          <button onClick={onChangeCustomer} data-testid="pos-gate-change"
                   className="text-[11px] text-[#2D5A27] hover:underline shrink-0">
            Change
          </button>
        </div>

        {/* Package + bag summary — the operator can eyeball what's about
            to be billed before pulling the trigger. */}
        {ctxLoading && (
          <div className="text-[11px] text-[#8A8780]" data-testid="pos-gate-loading">
            Loading customer package…
          </div>
        )}
        {!ctxLoading && (
          <div className="grid grid-cols-3 gap-2 text-center pt-1">
            <GateStat label="Package"
                       value={pkg ? pkg.name : "Ad-hoc"}
                       testid="pos-gate-pkg"/>
            <GateStat label="Assigned bags"
                       value={String(bagsN)}
                       testid="pos-gate-bags"/>
            <GateStat label="Extras avail."
                       value={String(extras)}
                       testid="pos-gate-extras"/>
          </div>
        )}
      </div>

      <button onClick={onStart} data-testid="pos-start-job-btn"
              disabled={ctxLoading}
              className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium inline-flex items-center justify-center gap-2">
        <CheckCircle size={16} weight="fill"/>
        Start Job
        <ArrowRight size={12} weight="bold"/>
      </button>
    </div>
  );
}

function GateStat({ label, value, testid }) {
  return (
    <div className="bg-[#F5F3EC] rounded-md py-2 px-1" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">{label}</div>
      <div className="text-sm font-semibold text-[#1A1A1A] truncate mt-0.5">{value}</div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Phone-first POS form — assigned QR bags, non-QR loose weights, extras
// (Iter 214)
// ═════════════════════════════════════════════════════════════════════
function PhonePosForm({ ctx, ctxLoading, jobStartedAt,
                          assignedBagWeights, setAssignedBagWeights,
                          nonQrWeights, setNonQrWeights,
                          selectedExtras, setSelectedExtras }) {
  // Iter 217 — small "Job started HH:MM" indicator at the top of the
  // form so the operator sees the ceremony took effect.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!jobStartedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(iv);
  }, [jobStartedAt]);
  const elapsedLabel = useMemo(() => {
    if (!jobStartedAt) return null;
    const s = Math.max(0, Math.floor((now - jobStartedAt) / 1000));
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }, [jobStartedAt, now]);

  if (ctxLoading) {
    return (
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-6 text-center text-sm text-[#8A8780]"
            data-testid="pos-ctx-loading">
        Fetching customer package & bags…
      </div>
    );
  }
  if (!ctx) return null;
  const assignedBags   = ctx.assigned_bags   || [];
  const includedTypes  = ctx.included_types  || [];
  const extras         = ctx.extra_charges   || [];
  const nonQrAllowed   = !!ctx.non_qr_allowed;
  const nonQrCats      = new Set(ctx.non_qr_categories || []);
  const nonQrTypes     = nonQrAllowed
    ? includedTypes.filter(t => nonQrCats.has(t.category))
    : [];

  const setBagW = (qr, v) => setAssignedBagWeights(prev => ({ ...prev, [qr]: v }));
  const setNqW  = (wtId, v) => setNonQrWeights(prev => ({ ...prev, [wtId]: v }));

  return (
    <div className="space-y-3" data-testid="pos-phone-form">
      {jobStartedAt && (
        <div className="flex items-center justify-between bg-[#EDF7EA] border border-[#B7D3AF] rounded-md px-3 py-2"
              data-testid="pos-job-elapsed">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#1F3A19]
                          inline-flex items-center gap-1.5">
            <CheckCircle size={12} weight="fill"/>Job in progress
          </div>
          <div className="text-[11px] text-[#1F3A19] font-mono">
            {elapsedLabel}
          </div>
        </div>
      )}
      {/* Package summary */}
      {ctx.package && (
        <div className="bg-[#F5F3EC] border border-[#E5E2D9] rounded-lg p-3"
              data-testid="pos-pkg-summary">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[#5C5A55]">
            <PackageIcon size={12}/>Assigned package
          </div>
          <div className="font-heading text-base mt-1">{ctx.package.name}</div>
          {(ctx.category_allowances || []).length > 0 && (
            <div className="text-[11px] text-[#5C5A55] mt-1">
              {(ctx.category_allowances || []).map(c => (
                <span key={c.category} className="inline-block mr-2">
                  <b>{c.category}</b>: {c.allowed_quantity_kg || 0} kg free
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* QR-coded bag collection */}
      {assignedBags.length > 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg" data-testid="pos-assigned-bags">
          <div className="px-3 py-2 border-b border-[#EFECE3] text-[11px] uppercase tracking-[0.16em] text-[#5C5A55] flex items-center gap-1.5">
            <QrCode size={12}/>QR-coded bags · {assignedBags.length} delivered
          </div>
          <div className="divide-y divide-[#F7F6F2]">
            {assignedBags.map(b => {
              const wt = includedTypes.find(t => t.waste_type_id === b.waste_type_id);
              return (
                <div key={b.qr_code} className="flex items-center gap-2 px-3 py-2.5"
                      data-testid={`pos-abag-${b.qr_code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm truncate">{b.qr_code}</div>
                    <div className="text-[10.5px] text-[#5C5A55] truncate">
                      {wt ? `${wt.category} · ${wt.waste_name}` : (b.bag_type || "—")}
                    </div>
                  </div>
                  <input type="number" step="0.01" min="0"
                          value={assignedBagWeights[b.qr_code] || ""}
                          onChange={e => setBagW(b.qr_code, e.target.value)}
                          placeholder="kg"
                          data-testid={`pos-abag-w-${b.qr_code}`}
                          className="w-20 h-9 px-2 border border-[#E5E2D9] rounded text-sm text-right font-mono"/>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Non-QR bag collection */}
      {nonQrTypes.length > 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg" data-testid="pos-non-qr">
          <div className="px-3 py-2 border-b border-[#EFECE3] text-[11px] uppercase tracking-[0.16em] text-[#5C5A55] flex items-center gap-1.5">
            <Scales size={12}/>Non-QR bags · loose weight
          </div>
          <div className="divide-y divide-[#F7F6F2]">
            {nonQrTypes.map(t => (
              <div key={t.waste_type_id} className="flex items-center gap-2 px-3 py-2.5"
                    data-testid={`pos-nq-${t.waste_type_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.waste_name}</div>
                  <div className="text-[10.5px] text-[#5C5A55] truncate">
                    {t.category} · ₹{t.customer_rate || t.rate_per_kg || 0}/kg
                  </div>
                </div>
                <input type="number" step="0.01" min="0"
                        value={nonQrWeights[t.waste_type_id] || ""}
                        onChange={e => setNqW(t.waste_type_id, e.target.value)}
                        placeholder="kg"
                        data-testid={`pos-nq-w-${t.waste_type_id}`}
                        className="w-20 h-9 px-2 border border-[#E5E2D9] rounded text-sm text-right font-mono"/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extra items — Iter 218: multi-add with quantity chips */}
      {extras.length > 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-3" data-testid="pos-extras">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#5C5A55] flex items-center gap-1.5 mb-2">
            <Plus size={12}/>Extra items
            <span className="text-[10px] normal-case tracking-normal text-[#8A8780] font-normal">
              — tap to add · tap again to add another
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {extras.map(x => {
              const label = x.type || x.name || "Extra";
              const count = selectedExtras.filter(e => e === x.id).length;
              const on = count > 0;
              return (
                <div key={x.id} data-testid={`pos-extra-${x.id}`}
                      className={`inline-flex items-stretch rounded-full text-xs border overflow-hidden ${
                        on
                          ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                          : "bg-[#F5F3EC] text-[#2D3033] border-[#E5E2D9]"
                      }`}>
                  {on && (
                    <button type="button" onClick={() => setSelectedExtras(prev => {
                              const i = prev.lastIndexOf(x.id);
                              if (i === -1) return prev;
                              return [...prev.slice(0, i), ...prev.slice(i + 1)];
                            })}
                             data-testid={`pos-extra-minus-${x.id}`}
                             title="Remove one"
                             className="px-2 hover:bg-[#3A7033]">−</button>
                  )}
                  <button type="button" onClick={() => setSelectedExtras(prev => [...prev, x.id])}
                           data-testid={`pos-extra-add-${x.id}`}
                           className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 ${
                             on ? "hover:bg-[#3A7033]" : "hover:border-[#2D5A27]"
                           }`}>
                    {on
                      ? <CheckCircle size={12} weight="fill"/>
                      : <Plus size={12} weight="bold"/>}
                    <span data-testid={`pos-extra-name-${x.id}`} className="whitespace-nowrap">
                      {label}
                    </span>
                    <span className="opacity-70 whitespace-nowrap">· ₹{Number(x.amount).toFixed(0)}</span>
                    {count > 1 && (
                      <span className="ml-1 bg-white/25 rounded px-1 text-[10px] font-semibold"
                             data-testid={`pos-extra-count-${x.id}`}>
                        ×{count}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hint when no QR bags AND no non-QR types are configured — the
          operator can still add a bag via the "Scan a bag" button below
          or just fire an extras-only invoice. */}
      {assignedBags.length === 0 && nonQrTypes.length === 0 && (
        <div className="bg-[#FDF3D0] border border-[#EFDCA1] rounded-md p-3 text-xs text-[#7A5A11]"
              data-testid="pos-form-empty-hint">
          <Warning size={12} weight="fill" className="inline -mt-0.5 mr-1"/>
          No delivered bags on file for this customer, and no non-QR
          categories are enabled in this package. Scan a bag below or
          add an extra item to bill.
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Customer picker (search)
// ═════════════════════════════════════════════════════════════════════
function CustomerPicker({ onCancel, onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      staffApi.get(`/staff-app/customers/search?q=${encodeURIComponent(q.trim())}`)
        .then(r => setRows(r.data || []))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={onCancel} data-testid="pos-picker-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg inline-flex items-center gap-1.5">
            <MagnifyingGlass size={16} />Pick a customer
          </div>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F7F6F2]">
            <X size={16} />
          </button>
        </div>
        <input data-testid="pos-picker-q" value={q} onChange={e => setQ(e.target.value)}
                placeholder="Name, phone, code, address…" autoFocus
                className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm mb-3"/>
        {loading && <div className="text-xs text-[#8A8780]">Searching…</div>}
        {!loading && q.trim().length < 2 && (
          <div className="text-xs text-[#8A8780]">Type at least 2 characters</div>
        )}
        {!loading && q.trim().length >= 2 && rows.length === 0 && (
          <div className="text-xs text-[#C25D38]">No customer matched.</div>
        )}
        <div className="space-y-2">
          {rows.map(c => (
            <button key={c.id} onClick={() => onPick(c, c.package)}
                    data-testid={`pos-picker-row-${c.id}`}
                    className="w-full text-left bg-white border border-[#E5E2D9] rounded-md p-3 hover:border-[#2D5A27]">
              <div className="font-heading text-sm truncate">{c.name}</div>
              <div className="text-[11px] text-[#5C5A55] font-mono">
                {c.customer_code || "—"} · {c.phone || "—"}
              </div>
              {c.address && (
                <div className="text-[11px] text-[#5C5A55] truncate mt-0.5">{c.address}</div>
              )}
              {c.package && (
                <div className="text-[10px] text-[#2D5A27] mt-1">
                  Package: <strong>{c.package.name}</strong>
                  {c.package.price ? ` · ₹${c.package.price}` : ""}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
